import { HttpClient, HttpParams } from '@angular/common/http'
import { Inject, Injectable } from '@angular/core'
import {
    BABOOR_ENVIRONMENT,
    BaboorEnvironmentConfig,
} from '@baboor/baboor-core'
import type {
    InventoryProduct,
    InsertInventoryProduct,
    UpdateInventoryProduct,
    InventoryCategory,
    InsertInventoryCategory,
    UpdateInventoryCategory,
    InventorySupplier,
    InsertInventorySupplier,
    UpdateInventorySupplier,
    StockMovement,
    PurchaseRecord,
    InventoryProductQueryParams,
} from '@baboor/baboor-models'
import { ApiResponse } from '@baboor/common-models'
import { toHttpParams } from '@baboor/common-util'
import { Observable } from 'rxjs'

@Injectable({ providedIn: 'root' })
export class InventoryApiService {
    private apiUrl: string

    constructor(
        private http: HttpClient,
        @Inject(BABOOR_ENVIRONMENT) private env: BaboorEnvironmentConfig,
    ) {
        this.apiUrl = `${env.apiUrl}/v1/inventory`
    }

    getProducts(
        params?: InventoryProductQueryParams,
    ): Observable<ApiResponse<InventoryProduct[]>> {
        return this.http.get<ApiResponse<InventoryProduct[]>>(
            `${this.apiUrl}/products`,
            { params: toHttpParams(params ?? {}) },
        )
    }

    getProduct(id: string): Observable<ApiResponse<InventoryProduct>> {
        return this.http.get<ApiResponse<InventoryProduct>>(
            `${this.apiUrl}/products/${id}`,
        )
    }

    createProduct(
        data: InsertInventoryProduct,
    ): Observable<ApiResponse<InventoryProduct>> {
        return this.http.post<ApiResponse<InventoryProduct>>(
            `${this.apiUrl}/products`,
            data,
        )
    }

    updateProduct(
        id: string,
        data: UpdateInventoryProduct,
    ): Observable<ApiResponse<InventoryProduct>> {
        return this.http.patch<ApiResponse<InventoryProduct>>(
            `${this.apiUrl}/products/${id}`,
            data,
        )
    }

    deleteProduct(id: string): Observable<ApiResponse<unknown>> {
        return this.http.delete<ApiResponse<unknown>>(
            `${this.apiUrl}/products/${id}`,
        )
    }

    archiveProduct(id: string): Observable<ApiResponse<InventoryProduct>> {
        return this.http.patch<ApiResponse<InventoryProduct>>(
            `${this.apiUrl}/products/${id}/archive`,
            {},
        )
    }

    getCategories(): Observable<ApiResponse<InventoryCategory[]>> {
        return this.http.get<ApiResponse<InventoryCategory[]>>(
            `${this.apiUrl}/categories`,
        )
    }

    createCategory(
        data: InsertInventoryCategory,
    ): Observable<ApiResponse<InventoryCategory>> {
        return this.http.post<ApiResponse<InventoryCategory>>(
            `${this.apiUrl}/categories`,
            data,
        )
    }

    updateCategory(
        id: string,
        data: UpdateInventoryCategory,
    ): Observable<ApiResponse<InventoryCategory>> {
        return this.http.patch<ApiResponse<InventoryCategory>>(
            `${this.apiUrl}/categories/${id}`,
            data,
        )
    }

    deleteCategory(id: string): Observable<ApiResponse<unknown>> {
        return this.http.delete<ApiResponse<unknown>>(
            `${this.apiUrl}/categories/${id}`,
        )
    }

    getSuppliers(): Observable<ApiResponse<InventorySupplier[]>> {
        return this.http.get<ApiResponse<InventorySupplier[]>>(
            `${this.apiUrl}/suppliers`,
        )
    }

    getSupplier(id: string): Observable<ApiResponse<InventorySupplier>> {
        return this.http.get<ApiResponse<InventorySupplier>>(
            `${this.apiUrl}/suppliers/${id}`,
        )
    }

    createSupplier(
        data: InsertInventorySupplier,
    ): Observable<ApiResponse<InventorySupplier>> {
        return this.http.post<ApiResponse<InventorySupplier>>(
            `${this.apiUrl}/suppliers`,
            data,
        )
    }

    updateSupplier(
        id: string,
        data: UpdateInventorySupplier,
    ): Observable<ApiResponse<InventorySupplier>> {
        return this.http.patch<ApiResponse<InventorySupplier>>(
            `${this.apiUrl}/suppliers/${id}`,
            data,
        )
    }

    deleteSupplier(id: string): Observable<ApiResponse<unknown>> {
        return this.http.delete<ApiResponse<unknown>>(
            `${this.apiUrl}/suppliers/${id}`,
        )
    }

    stockIn(
        productId: string,
        quantity: number,
        reason?: string,
    ): Observable<ApiResponse<StockMovement>> {
        return this.http.post<ApiResponse<StockMovement>>(
            `${this.apiUrl}/stock/in`,
            { productId, quantity, reason },
        )
    }

    stockOut(
        productId: string,
        quantity: number,
        reason?: string,
    ): Observable<ApiResponse<StockMovement>> {
        return this.http.post<ApiResponse<StockMovement>>(
            `${this.apiUrl}/stock/out`,
            { productId, quantity, reason },
        )
    }

    adjustStock(
        productId: string,
        quantity: number,
        reason?: string,
    ): Observable<ApiResponse<StockMovement>> {
        return this.http.post<ApiResponse<StockMovement>>(
            `${this.apiUrl}/stock/adjust`,
            { productId, quantity, reason },
        )
    }

    getStockMovements(
        productId: string,
    ): Observable<ApiResponse<StockMovement[]>> {
        return this.http.get<ApiResponse<StockMovement[]>>(
            `${this.apiUrl}/stock/movements/${productId}`,
        )
    }

    createPurchase(data: {
        supplierId?: string
        totalCost: string
        items: Array<{ productId: string; quantity: number; unitCost: string }>
    }): Observable<ApiResponse<PurchaseRecord>> {
        return this.http.post<ApiResponse<PurchaseRecord>>(
            `${this.apiUrl}/purchases`,
            data,
        )
    }

    getPurchases(): Observable<ApiResponse<PurchaseRecord[]>> {
        return this.http.get<ApiResponse<PurchaseRecord[]>>(
            `${this.apiUrl}/purchases`,
        )
    }

    getPurchase(id: string): Observable<ApiResponse<PurchaseRecord>> {
        return this.http.get<ApiResponse<PurchaseRecord>>(
            `${this.apiUrl}/purchases/${id}`,
        )
    }

    getLowStockAlerts(): Observable<ApiResponse<InventoryProduct[]>> {
        return this.http.get<ApiResponse<InventoryProduct[]>>(
            `${this.apiUrl}/alerts/low-stock`,
        )
    }
}
